import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { AnimatePresence } from 'motion/react';
import { X, CaretLeft, CaretRight, MapPin, Globe, Phone, FolderPlus, ArrowSquareOut } from '@phosphor-icons/react';
import api from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';
import CopyableId from './CopyableId';
import CollectionPickerModal from './CollectionPickerModal';
import ExplorationLinks from './ExplorationLinks';
import Modal, { ENTITY_PANEL_MOTION } from './Modal';

function getHostname(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

/**
 * COL-94: this modal now renders a grouped entity ("one entity = N mentions"
 * — see Entities.jsx's groupKey/groupedEntities) rather than a single
 * enrichment row. Structurally it mirrors PlaceDetailModal.jsx (sources
 * list with onOpenItem, multi-attribution creator notes, sibling lookup via
 * a parent callback instead of internal state) while keeping the
 * non-place-only sections PlaceDetailModal doesn't need: web-search
 * `answer`/`primary_result`/`additional_results`.
 */
export default function EntityDetailModal({
  group, onClose,
  onPrev, onNext, hasPrev = false, hasNext = false,
  itemIndex, totalItems,
  onOpenItem, onOpenEntity,
}) {
  const { targetRef: scrollRef, unlock: unlockScroll } = useScrollLock(true);

  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [unlockScroll, onClose]);

  const titleId = useId();
  const [siblings, setSiblings] = useState([]);
  const [sourceItemDetail, setSourceItemDetail] = useState(null);

  const {
    entity_name, entity_type, place_data = {}, enrichments = [], sources = [],
    // Built once by Entities.jsx during grouping (groupedEntities /
    // enrichmentToEntityGroup) rather than re-derived here on every
    // open/nav — mirrors how PlaceDetailModal reads group.creator_contexts.
    creator_contexts: creatorContexts = [],
  } = group;

  const primaryEnrichment = enrichments[0] || {};
  const {
    answer, primary_result, additional_results, exploration_links,
    item_id: primaryItemId, enrichment_id: primaryEnrichmentId,
  } = primaryEnrichment;

  const hasPlaceData = entity_type === 'place' && place_data && Object.keys(place_data).length > 0;

  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [collectionToast, setCollectionToast] = useState(null);
  const collectionToastRef = useRef(null);

  useEffect(() => {
    return () => {
      if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
    };
  }, []);

  const firstSource = sources[0];

  // COL-94: source_url isn't in the /enrichments payload (only item_headline/
  // item_summary/item_image are denormalized there) — fetch the full item,
  // same pattern PlaceDetailModal already uses. Silently no-ops on failure
  // (deleted/missing item) so the modal still renders — see COL-80 for the
  // analogous chat-card bug this is deliberately resilient against.
  useEffect(() => {
    setSourceItemDetail(null);
    if (!firstSource?.item_id) return;
    let cancelled = false;
    api.getItem(firstSource.item_id, { includeRawResponse: true })
      .then(data => {
        if (!cancelled) setSourceItemDetail(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [firstSource?.item_id]);

  // Fetch sibling entities from the primary mention's source item — clicking
  // one asks the parent to open its group (Entities.jsx's
  // handleOpenEntityFromSibling), not an internal swap, since a sibling may
  // belong to a different, not-yet-loaded group.
  useEffect(() => {
    setSiblings([]);
    if (!primaryEnrichment.item_id) return;
    let cancelled = false;
    api.getEnrichments({ itemId: primaryEnrichment.item_id, status: 'complete', limit: 50 })
      .then(data => {
        if (cancelled) return;
        const enrichmentIds = new Set(enrichments.map(e => e.enrichment_id));
        const others = (data.enrichments || []).filter(e => !enrichmentIds.has(e.enrichment_id));
        setSiblings(others);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryEnrichment.item_id]);

  const mapsUrl = hasPlaceData && place_data.formatted_address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place_data.formatted_address)}`
    : null;

  const sourceUrl = sourceItemDetail?.source_url;

  // Keyboard: arrows for prev/next entity. Escape-to-close is handled by
  // the shared <Modal> primitive.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight' && hasNext && onNext) { e.preventDefault(); onNext(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onPrev, onNext, hasPrev, hasNext]);

  return (
    <>
      <Modal
        onClose={handleClose}
        labelledBy={titleId}
        className="entity-detail-modal"
        panelMotionProps={ENTITY_PANEL_MOTION}
      >
        <button className="entity-detail-close" onClick={handleClose} aria-label="Close">
          <X size={20} />
        </button>

        {/* Prev/Next navigation */}
        {hasPrev && onPrev && (
          <button className="entity-detail-nav entity-detail-nav-prev" onClick={onPrev} aria-label="Previous entity">
            <CaretLeft size={20} />
          </button>
        )}
        {hasNext && onNext && (
          <button className="entity-detail-nav entity-detail-nav-next" onClick={onNext} aria-label="Next entity">
            <CaretRight size={20} />
          </button>
        )}
        {itemIndex !== undefined && totalItems !== undefined && (
          <div className="entity-detail-counter">
            {itemIndex + 1} of {totalItems}
          </div>
        )}

        <div className="entity-detail-scroll" ref={scrollRef}>
          {/* Place Hero Image — item screenshot only (COL-103: Google Places
              photos are no longer requested, stored or rendered) */}
          {hasPlaceData && firstSource?.item_image && (
            <div className="entity-detail-hero">
              <img src={firstSource.item_image} alt={entity_name} />
            </div>
          )}

          {/* Entity Header */}
          <div className="entity-detail-header">
            <h2 className="entity-detail-name" id={titleId}>{entity_name}</h2>
            <span className="enrichment-card-badge">{entity_type}</span>
          </div>

          {/* Creator Notes — one per mention with creator_context */}
          {creatorContexts.map((ctx, i) => (
            <div key={i} className="entity-detail-creator-context">
              <p style={{ fontStyle: 'italic', margin: 0, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                "{ctx.text}"
              </p>
              {ctx.item_headline && (
                <span
                  className="entity-detail-creator-source"
                  onClick={() => onOpenItem?.(ctx.item_id)}
                >
                  From {ctx.item_headline}
                </span>
              )}
            </div>
          ))}

          {/* Place Data */}
          {hasPlaceData && (
            <div className="entity-detail-place">
              {place_data.formatted_address && (
                <div className="entity-detail-place-row">
                  <MapPin size={14} />
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                    {place_data.formatted_address}
                  </a>
                </div>
              )}
              {place_data.website && (
                <div className="entity-detail-place-row">
                  <Globe size={14} />
                  <a href={place_data.website} target="_blank" rel="noopener noreferrer">
                    {getHostname(place_data.website)}
                  </a>
                </div>
              )}
              {place_data.phone_number && (
                <div className="entity-detail-place-row">
                  <Phone size={14} />
                  <span>{place_data.phone_number}</span>
                </div>
              )}
              {place_data.rating && (
                <div className="entity-detail-place-row">
                  <span style={{ fontSize: '14px' }}>★</span>
                  <span>{place_data.rating}{place_data.user_rating_count ? ` (${place_data.user_rating_count} reviews)` : ''}</span>
                </div>
              )}
            </div>
          )}

          {/* COL-127: Directions/Reviews deep-links, see ExplorationLinks.jsx */}
          <ExplorationLinks links={exploration_links} />

          {/* Answer */}
          {answer && (
            <div className="entity-detail-answer">
              <p>{answer}</p>
            </div>
          )}

          {/* Sources (web search results) */}
          {(primary_result || (additional_results && additional_results.length > 0)) && (
            <div className="entity-detail-sources">
              <h3 className="entity-detail-section-title">Sources</h3>
              {primary_result && (
                <div className="entity-detail-source-primary">
                  {primary_result.snippet && (
                    <p className="entity-detail-source-snippet">"{primary_result.snippet}"</p>
                  )}
                  <a href={primary_result.url} target="_blank" rel="noopener noreferrer" className="entity-detail-source-link">
                    {primary_result.title || getHostname(primary_result.url)}
                    <span className="entity-detail-source-domain">{getHostname(primary_result.url)}</span>
                  </a>
                </div>
              )}
              {additional_results && additional_results.length > 0 && (
                <ul className="entity-detail-source-list">
                  {additional_results.map((r, i) => (
                    <li key={i}>
                      <a href={r.url} target="_blank" rel="noopener noreferrer">
                        {r.title || getHostname(r.url)}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <hr className="entity-detail-divider" />

          {/* Parent Item(s) — clickable (COL-94), opens ItemDetailModal via
              the parent's onOpenItem, one card per mention when the entity
              was saved more than once. */}
          {sources.length > 0 && (
            <div className="entity-detail-parent">
              <h3 className="entity-detail-section-title">
                {sources.length === 1 ? 'From Screenshot' : `Referenced in ${sources.length} Items`}
              </h3>

              {/* COL-94: surface source_url as an external link to the
                  original post, next to the first mention's screenshot. */}
              {sourceUrl && (
                <div className="entity-detail-place-row" style={{ marginBottom: 'var(--space-sm)' }}>
                  <ArrowSquareOut size={14} />
                  <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                    View original post
                  </a>
                </div>
              )}

              {sources.map((src, i) => (
                <button
                  key={src.enrichment_id || i}
                  type="button"
                  className="entity-detail-parent-card entity-detail-parent-card-button card-button-reset"
                  onClick={() => onOpenItem?.(src.item_id)}
                >
                  {src.item_image && (
                    <div className="entity-detail-parent-thumb">
                      <img src={src.item_image} alt="" />
                    </div>
                  )}
                  <div className="entity-detail-parent-info">
                    {src.item_headline && <div className="entity-detail-parent-headline">{src.item_headline}</div>}
                    {src.item_summary && <div className="entity-detail-parent-summary">{src.item_summary}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Sibling Entities */}
          {siblings.length > 0 && (
            <div className="entity-detail-siblings">
              <h3 className="entity-detail-section-title">Other Entities from this Item</h3>
              <div className="entity-detail-siblings-grid">
                {siblings.map(s => (
                  <button
                    key={s.enrichment_id}
                    className="entity-detail-sibling-card"
                    onClick={() => onOpenEntity?.(s)}
                  >
                    <span className="enrichment-card-badge" style={{ fontSize: '0.625rem' }}>{s.entity_type}</span>
                    <span className="entity-detail-sibling-name">{s.entity_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add to Collection — adds every mention in this group */}
          {enrichments.length > 0 && (
            <div style={{ marginTop: 'var(--space-md)' }}>
              <button
                className="btn btn-ghost btn-sm btn-add-to-collection"
                onClick={() => setShowCollectionPicker(true)}
              >
                <FolderPlusIcon /> Add to collection
              </button>
              {collectionToast && (
                <div style={{ marginTop: 'var(--space-xs)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {collectionToast}
                </div>
              )}
            </div>
          )}

          {/* IDs (primary mention) */}
          {(primaryItemId || primaryEnrichmentId) && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              {primaryItemId && (
                <>
                  <h3 className="entity-detail-section-title">Item ID</h3>
                  <CopyableId value={primaryItemId} />
                </>
              )}
              {primaryEnrichmentId && (
                <div style={{ marginTop: primaryItemId ? 'var(--space-sm)' : 0 }}>
                  <h3 className="entity-detail-section-title">Enrichment ID</h3>
                  <CopyableId value={primaryEnrichmentId} />
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <AnimatePresence>
        {showCollectionPicker && (
          <CollectionPickerModal
            items={enrichments.map(e => ({ item_id: e.enrichment_id, item_type: 'enrichment' }))}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={(msg) => {
              setCollectionToast(msg);
              if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
              collectionToastRef.current = setTimeout(() => setCollectionToast(null), 3000);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function FolderPlusIcon() {
  return <FolderPlus size={14} />;
}
